import { HTMLInputTypeAttribute, ReactNode } from 'react';
import useCustomTranslation from '@hooks/useCustomTranslation';
import { Grid2, TextField, Typography } from '@mui/material';
import { Controller, Control, FieldValues, Path, FieldError } from 'react-hook-form';
import { EMPTY_STRING } from '@constants';
import TooltipIcon from '@components/TooltipIcon';

import styles from './ControlledTextField.module.scss';

interface ControlledTextFieldProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  rows?: number;
  size?: 'small' | 'medium';
  additionalOnChange?: () => void;
  placeholder?: string;
  onBlur?: () => void;
  helperText?: string;
  label?: string;
  errorText?: string;
  isFullWidth?: boolean;
  tooltipText?: ReactNode;
  disabled?: boolean;
  multiline?: boolean;
  minRows?: number;
  rulesRequired?: boolean;
  type?: HTMLInputTypeAttribute;
}

const ControlledTextField = <T extends FieldValues>({
  name,
  control,
  rows,
  size = 'small',
  placeholder,
  additionalOnChange,
  onBlur,
  helperText,
  label,
  errorText,
  isFullWidth,
  tooltipText,
  disabled,
  multiline,
  minRows,
  rulesRequired = true,
  type,
}: ControlledTextFieldProps<T>) => {
  const { getTranslation } = useCustomTranslation();

  const getHelperText = (error?: FieldError) => {
    if (!error) {
      return helperText;
    }

    if (errorText) {
      return errorText;
    }

    if (error.message?.includes('CvaInput.') || error.message?.includes('CvaProj.')) {
      return getTranslation(error.message || EMPTY_STRING, `validationIntegration.${error.message}`);
    }

    return error.message;
  };

  return (
    <Grid2 container gap={0.3} className={isFullWidth ? styles.fullWidth : EMPTY_STRING}>
      {label && (
        <div className={styles.labelContainer}>
          <Typography variant="header" color="textSecondary">
            {label}
          </Typography>
          {tooltipText && <TooltipIcon title={tooltipText} />}
        </div>
      )}
      <Controller
        name={name}
        control={control}
        rules={{ required: rulesRequired ? getTranslation('CvaInput.Home104', 'validation.fieldRequiredText') : false }}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <TextField
            {...field}
            multiline={multiline ?? Boolean(rows || minRows)}
            fullWidth
            error={!!error}
            helperText={getHelperText(error)}
            rows={rows}
            minRows={minRows}
            size={size}
            type={type}
            disabled={disabled}
            inputRef={ref}
            placeholder={placeholder}
            value={field.value || EMPTY_STRING}
            onChange={(newValue) => {
              field.onChange(newValue);
              if (additionalOnChange) {
                additionalOnChange();
              }
            }}
            onBlur={() => {
              field.onBlur();
              if (onBlur) {
                onBlur();
              }
            }}
          />
        )}
      />
    </Grid2>
  );
};

export default ControlledTextField;
